import type { Fingering, PrerenderedPhraseData } from 'Types';

/** A finger number computed for one drawn note, with its 0-100 need score. */
export type NoteFingering = {
  finger: number;
  need: number;
};

type PrerenderedNote = PrerenderedPhraseData['currentVoiceEntries'][number]['Notes'][number];

const COMPUTED_FINGERING_TYPE = 'Fingering';

/** The id a drawn note is addressed by; unique within its phrase. */
export const noteIdOf = (note: PrerenderedNote): string | undefined =>
  note.graphics[0]?.staveNote.id;

const toDrawnFingering = (note: PrerenderedNote, computed: NoteFingering): Fingering => ({
  placement: note.staffPosition === 'top' ? 'Above' : 'Below',
  type: COMPUTED_FINGERING_TYPE,
  value: `${computed.finger}`,
});

const withComputedFingering = (
  note: PrerenderedNote,
  fingeringByNoteId: ReadonlyMap<string, NoteFingering>,
): PrerenderedNote => {
  const noteId = noteIdOf(note);
  const computed = noteId === undefined ? undefined : fingeringByNoteId.get(noteId);
  return {
    ...note,
    fingering: computed ? toDrawnFingering(note, computed) : undefined,
    fingeringNeed: computed?.need,
  };
};

/**
 * The phrase's notes with their finger numbers replaced by the computed ones.
 *
 * It replaces rather than merges: a note missing from `fingeringByNoteId` ends
 * up with no number, so a number carried by the phrase file never survives next
 * to a computed one.
 */
export const replaceFingering = (
  serializedIterator: PrerenderedPhraseData[],
  fingeringByNoteId: ReadonlyMap<string, NoteFingering>,
): PrerenderedPhraseData[] =>
  serializedIterator.map((phraseSlice) => ({
    ...phraseSlice,
    currentVoiceEntries: phraseSlice.currentVoiceEntries.map((voiceEntry) => ({
      ...voiceEntry,
      Notes: voiceEntry.Notes.map((note) => withComputedFingering(note, fingeringByNoteId)),
    })),
  }));
